import { InteractionType } from '../input/InputManager.js';
import { Logger } from '../logging/Logger.js';
/**
 * Input gatherer is responsible for collecting user input such as mouse or touch events from the canvas.
 * Normalize this input and send it to the input manager.
 * The architecture is built on the asumption that a user of the viewer should be able to write there own gatherer.
 * This enables users to implement advanced input schemas while we can maintain simple interaction logic in the viewer.
 */
export class InputGatherer {
    constructor(_canvas, _viewer, active_view, config) {
        this._canvas = _canvas;
        this._viewer = _viewer;
        this._canvas_width_normalizer = 1;
        this._canvas_height_normalizer = 1;
        /**
         * Here we need to keep track of the two first pointers on touch setups (or mouse + touch on setups that support that)
         * This is so that we can support gestures such as pinch (zoom)
         */
        this._pointer_1 = null;
        this._pointer_2 = null;
        this._is_dragging = false;
        this._pinch_square_distance = 0;
        this._multi_touch_position = null;
        this._active_view = null;
        this._event_listeners = [];
        this.on_pointerenter = (evt) => {
            const pointer = this.get_active_pointer(evt);
            if (pointer) {
                // Update pointer positions, to not get any "jumps" when the pointer re-enters
                pointer.start_position = { x: evt.offsetX, y: evt.offsetY };
                pointer.x = evt.offsetX;
                pointer.y = evt.offsetY;
                // Run the pointerup event handler to properly handle the case where the mouse button has been released.
                if ((pointer.type === InteractionType.PRIMARY && (evt.buttons & 1) === 0) ||
                    (pointer.type === InteractionType.SECONDARY && (evt.buttons & 2) === 0)) {
                    this.on_pointerup(evt);
                }
            }
        };
        this.on_pointerdown = (evt) => {
            var _a, _b;
            if (this._pointer_1 && this._pointer_2)
                return;
            if ((_a = this._active_view) === null || _a === void 0 ? void 0 : _a.input_manager.is_dragging_object)
                return;
            let type = InteractionType.INVALID;
            if (evt.pointerType === 'touch' || evt.pointerType === 'pen')
                type = InteractionType.TOUCH;
            else if (evt.pointerType === 'mouse') {
                if (evt.button === 0)
                    type = InteractionType.PRIMARY;
                else if (evt.button === 2)
                    type = InteractionType.SECONDARY;
            }
            if (type !== InteractionType.INVALID) {
                const { offsetX: x, offsetY: y } = evt;
                const pointer = {
                    x: x,
                    y: y,
                    pointer_id: evt.pointerId,
                    type: type,
                    start_position: { x: x, y: y },
                    start_time: Date.now()
                };
                const clicked_view = this._viewer.get_view_at_canvas_coordinates(x * this.client_to_canvas_ratio.x, y * this.client_to_canvas_ratio.y);
                if (clicked_view !== null && clicked_view.auto_capture_input_focus) {
                    this.set_active_view(clicked_view);
                }
                if (!this._pointer_1) {
                    this._pointer_1 = pointer;
                    (_b = this._active_view) === null || _b === void 0 ? void 0 : _b.input_manager.interaction_begin({ canvas_x: x * this.client_to_canvas_ratio.x, canvas_y: y * this.client_to_canvas_ratio.y });
                }
                else if (!this._pointer_2) {
                    this._pointer_2 = pointer;
                }
            }
        };
        this.on_pointerup = (evt) => {
            var _a, _b, _c, _d, _e, _f, _g;
            const pointer = this.get_active_pointer(evt);
            if (!pointer)
                return;
            // Handle multi-touch input
            if (this._pointer_1 && ((_a = this._pointer_2) === null || _a === void 0 ? void 0 : _a.pointer_id) === evt.pointerId) {
                if (this._is_dragging && this.config.enable_multitouch_pan && this._multi_touch_position) {
                    (_b = this._active_view) === null || _b === void 0 ? void 0 : _b.input_manager.pan_end(this.make_drag_event(evt, this._pointer_1));
                }
                this._pointer_2 = null;
            }
            else if (this._pointer_2 && ((_c = this._pointer_1) === null || _c === void 0 ? void 0 : _c.pointer_id) === evt.pointerId) {
                if (this._is_dragging && this.config.enable_multitouch_pan && this._multi_touch_position) {
                    (_d = this._active_view) === null || _d === void 0 ? void 0 : _d.input_manager.pan_end(this.make_drag_event(evt, this._pointer_2));
                }
                this._pointer_1 = this._pointer_2;
                this._pointer_2 = null;
            }
            else {
                // Handle single point input
                if (this._is_dragging) {
                    if (pointer.type === InteractionType.TOUCH || pointer.type === InteractionType.PRIMARY) {
                        (_e = this._active_view) === null || _e === void 0 ? void 0 : _e.input_manager.drag_end(this.make_drag_event(evt, pointer));
                    }
                    else if (this.config.enable_right_mouse_click && pointer.type === InteractionType.SECONDARY) {
                        (_f = this._active_view) === null || _f === void 0 ? void 0 : _f.input_manager.pan_end(this.make_drag_event(evt, pointer));
                    }
                }
                else if (!this.config.enable_right_mouse_click && pointer.type === InteractionType.SECONDARY) {
                    return;
                }
                else {
                    (_g = this._active_view) === null || _g === void 0 ? void 0 : _g.input_manager.click({
                        type: pointer.type,
                        canvas_x: evt.offsetX * this.client_to_canvas_ratio.x,
                        canvas_y: evt.offsetY * this.client_to_canvas_ratio.y,
                        duration_ms: Date.now() - pointer.start_time
                    });
                }
                this._pointer_1 = null;
                this._pointer_2 = null;
                this._is_dragging = false;
            }
            this._multi_touch_position = null;
            this._pinch_square_distance = 0;
        };
        this.on_pointermove = (evt) => {
            var _a, _b, _c, _d, _e, _f, _g, _h, _j, _k;
            if (evt.pointerType === 'mouse') {
                (_a = this._active_view) === null || _a === void 0 ? void 0 : _a.input_manager.mouse_pointer_move({
                    canvas_x: evt.offsetX * this.client_to_canvas_ratio.x,
                    canvas_y: evt.offsetY * this.client_to_canvas_ratio.y
                });
            }
            const pointer = this.get_active_pointer(evt);
            if (!pointer) {
                if (evt.pointerType === 'touch') {
                    // Fire on pointer down, for triple-finger gestures on touch
                    this.on_pointerdown(evt);
                }
                return;
            }
            const is_dragging_object = (_b = this._active_view) === null || _b === void 0 ? void 0 : _b.input_manager.is_dragging_object;
            if (is_dragging_object && evt.pointerId !== ((_c = this._pointer_1) === null || _c === void 0 ? void 0 : _c.pointer_id))
                return;
            // Handle single point input
            if (this._pointer_1 && this._pointer_2 === null) {
                if (this._is_dragging) {
                    if (pointer.type === InteractionType.TOUCH || pointer.type === InteractionType.PRIMARY) {
                        (_d = this._active_view) === null || _d === void 0 ? void 0 : _d.input_manager.drag_move(this.make_drag_event(evt, pointer));
                    }
                    else if (pointer.type === InteractionType.SECONDARY && this.config.enable_right_mouse_pan) {
                        (_e = this._active_view) === null || _e === void 0 ? void 0 : _e.input_manager.pan_move(this.make_drag_event(evt, pointer));
                    }
                }
                else {
                    const offset_x = evt.offsetX - pointer.start_position.x;
                    const offset_y = evt.offsetY - pointer.start_position.y;
                    const delta_move = Math.sqrt(offset_x * offset_x + offset_y * offset_y);
                    if (delta_move > this.config.single_pointer_drag_threshold) {
                        this._is_dragging = true;
                        if (pointer.type === InteractionType.TOUCH || pointer.type === InteractionType.PRIMARY) {
                            (_f = this._active_view) === null || _f === void 0 ? void 0 : _f.input_manager.drag_begin(this.make_drag_event(evt, pointer));
                        }
                        else if (pointer.type === InteractionType.SECONDARY && this.config.enable_right_mouse_pan) {
                            (_g = this._active_view) === null || _g === void 0 ? void 0 : _g.input_manager.pan_begin(this.make_drag_event(evt, pointer));
                        }
                    }
                }
                // Update pointer data
                pointer.x = evt.offsetX;
                pointer.y = evt.offsetY;
                // Handle multi-touch input
            }
            else if (this._pointer_1 && this._pointer_2) {
                // Update pointer data
                pointer.x = evt.offsetX;
                pointer.y = evt.offsetY;
                // Handle multi-touch zoom
                const dist_x = this._pointer_1.x - this._pointer_2.x;
                const dist_y = this._pointer_1.y - this._pointer_2.y;
                const pinch_squared_distance = dist_x * dist_x + dist_y * dist_y;
                if (this._pinch_square_distance && this.config.enable_multitouch_zoom) {
                    const diff = pinch_squared_distance - this._pinch_square_distance;
                    const delta = diff / this.config.multitouch_zoom_sensitivity;
                    (_h = this._active_view) === null || _h === void 0 ? void 0 : _h.input_manager.zoom({
                        delta: -delta
                    });
                }
                // Handle multi-touch pan
                const multi_touch_position = {
                    x: (this._pointer_1.x + this._pointer_2.x) / 2,
                    y: (this._pointer_1.y + this._pointer_2.y) / 2
                };
                if (this._multi_touch_position && this.config.enable_multitouch_pan) {
                    const custom_event = { offsetX: multi_touch_position.x, offsetY: multi_touch_position.y };
                    if (this._is_dragging) {
                        (_j = this._active_view) === null || _j === void 0 ? void 0 : _j.input_manager.pan_move(this.make_drag_event(custom_event, this._multi_touch_position));
                    }
                    else {
                        (_k = this._active_view) === null || _k === void 0 ? void 0 : _k.input_manager.pan_begin(this.make_drag_event(custom_event, this._multi_touch_position));
                    }
                    this._is_dragging = true;
                }
                this._pinch_square_distance = pinch_squared_distance;
                this._multi_touch_position = multi_touch_position;
            }
        };
        this.on_pointercancel = (evt) => {
            var _a, _b, _c, _d, _e;
            if (!this._multi_touch_position && this._is_dragging) {
                if (((_a = this._pointer_1) === null || _a === void 0 ? void 0 : _a.type) === InteractionType.TOUCH || ((_b = this._pointer_1) === null || _b === void 0 ? void 0 : _b.type) === InteractionType.PRIMARY) {
                    (_c = this._active_view) === null || _c === void 0 ? void 0 : _c.input_manager.drag_end(this.make_drag_event(evt, this._pointer_1));
                }
                else if (((_d = this._pointer_1) === null || _d === void 0 ? void 0 : _d.type) === InteractionType.SECONDARY && this.config.enable_right_mouse_pan) {
                    (_e = this._active_view) === null || _e === void 0 ? void 0 : _e.input_manager.pan_end(this.make_drag_event(evt, this._pointer_1));
                }
            }
            this._pointer_1 = null;
            this._pointer_2 = null;
            this._is_dragging = false;
            this._multi_touch_position = null;
            this._pinch_square_distance = 0;
        };
        this.on_wheel = (evt) => {
            var _a;
            const clicked_view = this._viewer.get_view_at_canvas_coordinates(evt.offsetX * this.client_to_canvas_ratio.x, evt.clientY * this.client_to_canvas_ratio.y);
            if (clicked_view !== null && clicked_view.auto_capture_input_focus) {
                this.set_active_view(clicked_view);
            }
            /**
               * Wheel events in browser has historically been very unstable in that the values given vary by browser/OS.
               * According to several sources browsers are converging on these values.
               * More on the issues can be found in the following links
               * https://github.com/goxjs/glfw/issues/10
               * https://github.com/facebookarchive/fixed-data-table/blob/master/src/vendor_upstream/dom/normalizeWheel.js  Old but interesting
               * https://github.com/timmywil/panzoom/issues/560
               *
               * For now we will just forward value as is and try and fix as we find where it diverges
               */
            (_a = this._active_view) === null || _a === void 0 ? void 0 : _a.input_manager.zoom({
                delta: evt.deltaY
            });
        };
        this._active_view = active_view === undefined ? _viewer.get_default_view() : active_view;
        this.config = this.parse_config(config);
        // When sending the canvas to Babylon, which we did previously, they set some attributes to prevent site from zooming when pinching, among other things.
        // So here we do the same things manually.
        if (this._canvas.setAttribute) {
            this._canvas.setAttribute('touch-action', 'none');
            this._canvas.style.touchAction = 'none';
            this._canvas.style.webkitTapHighlightColor = 'transparent';
        }
        else {
            Logger.as('Input Gatherer').warn("Unable to use setAttribute on canvas. Pinching may not work on touch screens. Please set 'touch-action' to 'none' on the canvas");
        }
        this.calcualte_canvas_size_normalizer();
        this.register_listeners();
    }
    register_listeners() {
        if (window.PointerEvent) {
            // Pointer events
            this._event_listeners.push({ type: 'pointerdown', handler: this.on_pointerdown });
            this._event_listeners.push({ type: 'pointerup', handler: this.on_pointerup });
            this._event_listeners.push({ type: 'pointermove', handler: this.on_pointermove });
            this._event_listeners.push({ type: 'pointercancel', handler: this.on_pointercancel });
            this._event_listeners.push({ type: 'pointerenter', handler: this.on_pointerenter });
        }
        else {
            // Mouse events
            this._event_listeners.push({ type: 'mousedown', handler: (evt) => this.on_pointerdown(this.make_pointer_input_event(evt)) });
            this._event_listeners.push({ type: 'mouseup', handler: (evt) => this.on_pointerup(this.make_pointer_input_event(evt)) });
            this._event_listeners.push({ type: 'mousemove', handler: (evt) => this.on_pointermove(this.make_pointer_input_event(evt)) });
            // Touch events
            this._event_listeners.push({ type: 'touchstart', handler: (evt) => this.on_pointerdown(this.make_pointer_input_event(evt)) });
            this._event_listeners.push({ type: 'touchend', handler: (evt) => this.on_pointerup(this.make_pointer_input_event(evt)) });
            this._event_listeners.push({ type: 'touchmove', handler: (evt) => this.on_pointermove(this.make_pointer_input_event(evt)) });
            this._event_listeners.push({ type: 'touchcancel', handler: (evt) => this.on_pointercancel(this.make_pointer_input_event(evt)) });
        }
        // Wheel event
        this._event_listeners.push({ type: 'wheel', handler: this.on_wheel });
        // Add event listeners
        this._event_listeners.forEach(pair => this.add_event_listener(pair));
        // Prevent the context menu from showing
        this._canvas.oncontextmenu = function (e) {
            e.preventDefault();
        };
        this._viewer.get_viewer_resize_observable().add(() => {
            this.calcualte_canvas_size_normalizer();
        });
    }
    set_active_view(view) {
        var _a, _b, _c, _d, _e, _f, _g;
        if (view === this._active_view)
            return;
        (_a = this._active_view) === null || _a === void 0 ? void 0 : _a.input_manager.clear_hovering_interactable();
        if (this._is_dragging && !this._multi_touch_position) {
            if (((_b = this._pointer_1) === null || _b === void 0 ? void 0 : _b.type) === InteractionType.TOUCH || ((_c = this._pointer_1) === null || _c === void 0 ? void 0 : _c.type) === InteractionType.PRIMARY) {
                (_d = this._active_view) === null || _d === void 0 ? void 0 : _d.input_manager.drag_end(this.make_drag_event({ offsetX: this._pointer_1.x, offsetY: this._pointer_1.y }, this._pointer_1));
            }
            else if (this.config.enable_right_mouse_click && ((_e = this._pointer_1) === null || _e === void 0 ? void 0 : _e.type) === InteractionType.SECONDARY) {
                (_f = this._active_view) === null || _f === void 0 ? void 0 : _f.input_manager.pan_end(this.make_drag_event({ offsetX: this._pointer_1.x, offsetY: this._pointer_1.y }, this._pointer_1));
            }
        }
        else if (this._is_dragging && this._multi_touch_position && this.config.enable_multitouch_pan) {
            (_g = this._active_view) === null || _g === void 0 ? void 0 : _g.input_manager.pan_end(this.make_drag_event({ offsetX: this._multi_touch_position.x, offsetY: this._multi_touch_position.y }, this._multi_touch_position));
        }
        this._is_dragging = false;
        this._pointer_1 = null;
        this._pointer_2 = null;
        this._is_dragging = false;
        this._multi_touch_position = null;
        this._pinch_square_distance = 0;
        this._active_view = view;
    }
    dispose() {
        // Set the active view to null to "clear" the old one and end any lingering events.
        this.set_active_view(null);
        this._event_listeners.forEach(pair => this.remove_event_listener(pair));
        this._event_listeners.length = 0;
    }
    parse_config(config = {}) {
        return {
            single_pointer_drag_threshold: config.single_pointer_drag_threshold || 3,
            multitouch_zoom_sensitivity: config.multitouch_zoom_sensitivity || 100,
            enable_right_mouse_pan: config.enable_right_mouse_pan !== false,
            enable_right_mouse_click: config.enable_right_mouse_click !== false,
            enable_multitouch_zoom: config.enable_multitouch_zoom !== false,
            enable_multitouch_pan: config.enable_multitouch_pan !== false
        };
    }
    calcualte_canvas_size_normalizer() {
        this._canvas_width_normalizer = 1.0 / this._canvas.clientWidth;
        this._canvas_height_normalizer = 1.0 / this._canvas.clientHeight;
    }
    get client_to_canvas_ratio() {
        return {
            x: this._canvas.width / this._canvas.clientWidth,
            y: this._canvas.height / this._canvas.clientHeight
        };
    }
    get_active_pointer(evt) {
        var _a, _b;
        if (((_a = this._pointer_1) === null || _a === void 0 ? void 0 : _a.pointer_id) === evt.pointerId) {
            return this._pointer_1;
        }
        else if (((_b = this._pointer_2) === null || _b === void 0 ? void 0 : _b.pointer_id) === evt.pointerId) {
            return this._pointer_2;
        }
        return null;
    }
    make_pointer_input_event(evt) {
        if (evt instanceof MouseEvent) {
            return {
                pointerType: 'mouse',
                pointerId: 1,
                button: evt.button,
                offsetX: evt.offsetX,
                offsetY: evt.offsetY
            };
        }
        else if (evt instanceof TouchEvent) {
            const touch = evt.changedTouches[0];
            const canvas_rect = this._canvas.getBoundingClientRect();
            return {
                pointerType: 'touch',
                pointerId: touch.identifier,
                button: 0,
                offsetX: touch.clientX - canvas_rect.left,
                offsetY: touch.clientY - canvas_rect.top
            };
        }
        else {
            throw new Error('Invalid event type. Expected MouseEvent or TouchEvent.');
        }
    }
    make_drag_event(evt, start) {
        return {
            canvas_x: evt.offsetX * this.client_to_canvas_ratio.x,
            canvas_y: evt.offsetY * this.client_to_canvas_ratio.y,
            delta_x: (evt.offsetX - start.x) * this._canvas_width_normalizer,
            delta_y: (evt.offsetY - start.y) * this._canvas_height_normalizer
        };
    }
    add_event_listener(pair) {
        this._canvas.addEventListener(pair.type, pair.handler, false);
    }
    remove_event_listener(pair) {
        this._canvas.removeEventListener(pair.type, pair.handler);
    }
}
//# sourceMappingURL=InputGatherer.js.map